// src/vdr/dataroom.ts
import * as XLSX from "xlsx";
import type { IndexRow } from "./providers";

// Accepts codes like "1", "1.1", "1.1.2" (pure digits + dots)
const CODE_RE = /^\d+(?:\.\d+)*$/;

/**
 * DataRoom CSV Structure:
 * - Column B (index 1): Code (e.g., "1", "1.1", "1.2.3")
 * - Column C (index 2): Name (may include code prefix like "4.1_Operational data")
 * - Column E (index 4): Type ("folder" or "document")
 */

// --- CSV parsing (positional columns B, C, E) ---
function parseCsv(text: string): IndexRow[] {
  const lines = text.split(/\r?\n/).filter(l => l.trim().length);
  if (!lines.length) return [];

  const rows: IndexRow[] = [];
  let currentFileroom: string | null = null;

  // Skip first line (header)
  for (let i = 1; i < lines.length; i++) {
    const cols = safeSplit(lines[i]).map(c => c.trim());

    // Column B = code (index 1), Column C = name (index 2), Column E = type (index 4)
    const code = (cols[1] ?? "").toString().trim();
    const rawName = (cols[2] ?? "").toString().trim();
    const typeCell = (cols[4] ?? "").toString().trim().toLowerCase();

    if (typeCell === "fileroom") {
      currentFileroom = rawName.trim();
      rows.push({ code: "__fileroom", title: currentFileroom, meta: { isFileRoom: true } });
      continue;
    }

    if (!code || !CODE_RE.test(code)) continue;
    if (!typeCell || (typeCell !== "folder" && typeCell !== "document")) continue;

    const title = cleanName(rawName, code);
    const isDocument = typeCell === "document";

    rows.push({ code, title, meta: { isDocument, ...(currentFileroom ? { rootFolderName: currentFileroom } : {}) } });
  }

  return rows;
}

// --- XLS/XLSX parsing (positional columns B, C, E) ---
async function parseXlsx(file: File): Promise<IndexRow[]> {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array" });
  const ws = wb.Sheets[wb.SheetNames[0]];
  // header:1 gives us a 2D array of rows, preserving raw positions
  const table: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });

  const rows: IndexRow[] = [];
  let currentFileroom: string | null = null;

  // Skip first row (header)
  for (let i = 1; i < table.length; i++) {
    const row = table[i] ?? [];
    const code = String(row[1] ?? "").trim();      // Column B
    const rawName = String(row[2] ?? "").trim();   // Column C
    const typeCell = String(row[4] ?? "").trim().toLowerCase(); // Column E

    if (typeCell === "fileroom") {
      currentFileroom = rawName.trim();
      rows.push({ code: "__fileroom", title: currentFileroom, meta: { isFileRoom: true } });
      continue;
    }

    if (!code || !CODE_RE.test(code)) continue;
    if (!typeCell || (typeCell !== "folder" && typeCell !== "document")) continue;

    const title = cleanName(rawName, code);
    const isDocument = typeCell === "document";

    rows.push({ code, title, meta: { isDocument, ...(currentFileroom ? { rootFolderName: currentFileroom } : {}) } });
  }

  return rows;
}

/**
 * Clean the name by:
 * 1. Stripping leading code prefix (e.g., "4.1_Operational data" → "Operational data")
 * 2. Handling both underscore and space separators (e.g., "4.1_" or "4.1 ")
 * 3. If cleaning results in empty string, return original rawName
 */
function cleanName(rawName: string, code: string): string {
  if (!rawName) return "";

  // Build regex to match code with either "_" or " " separator
  // E.g., for code "4.1": /^4\.1[_ ]/
  const escapedCode = code.replace(/\./g, "\\.");
  const prefixPattern = new RegExp(`^${escapedCode}[_ ]`);

  let cleaned = rawName;
  if (prefixPattern.test(rawName)) {
    // Strip the code prefix
    cleaned = rawName.replace(prefixPattern, "").trim();
  }

  // If cleaning resulted in empty string, use the raw name
  // This handles cases where Column C is just "4.1" with no description
  return cleaned.length > 0 ? cleaned : rawName;
}

/**
 * Parse a DataRoom index file (CSV/XLS/XLSX) into normalized rows.
 * - Column B = Code (hierarchical like "1", "1.1", "1.2.3")
 * - Column C = Name (may include code prefix like "4.1_Name" or "4.1 Name")
 * - Column E = Type ("folder" or "document")
 * - Keeps both folders and documents, marking documents with meta.isDocument
 */
export async function parseIndexFile(file: File): Promise<IndexRow[]> {
  const ext = file.name.toLowerCase().split(".").pop();
  console.log(`🧾 [dataroom] parseIndexFile: name="${file.name}" ext="${ext}" size=${(file as any)?.size ?? "?"}`);

  let rows: IndexRow[] = [];
  if (ext === "csv") {
    const text = await file.text();
    rows = parseCsv(text);
  } else if (ext === "xlsx" || ext === "xls") {
    rows = await parseXlsx(file);
  } else {
    // Fallback: treat as CSV text
    const text = await file.text();
    rows = parseCsv(text);
  }

  console.log(`   ↳ parsed rows: ${rows.length}`);
  if (rows.length > 0) {
    const sample = rows.slice(0, Math.min(3, rows.length));
    console.log("   ↳ sample:", sample);
  } else {
    console.warn("   ⚠️ No valid DataRoom rows parsed. Expected columns: B=Code, C=Name, E=Type");
  }
  return rows;
}

/**
 * Simple CSV splitter for common cases.
 * Handles basic quoted fields (reused from Intralinks).
 */
function safeSplit(line: string): string[] {
  const out: string[] = [];
  let cur = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];

    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        // Escaped quote
        cur += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === "," && !inQuotes) {
      out.push(cur);
      cur = "";
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out;
}
